import { useCallback, useContext, useMemo, useState, useEffect } from "react";
import { CoreContext } from "context/CoreContext";
import { Delete, Read } from "services/core";
import { apiRequest, exposeStrapiError } from "utils";
import { languages } from "utils/options";

const CATEGORY_LIST_POPULATE = {
    courses: ['image', 'tags', 'tag_types', 'title_translations', 'categories.title_translations', 'sub_categories.title_translations'],
    'partners-and-staffs': ['image', 'tags', 'tag_types', 'title_translations', 'categories.title_translations', 'sub_categories.title_translations'],
    'support-materials': ['image', 'document', 'tags', 'tag_types', 'title_translations', 'categories.title_translations', 'sub_categories.title_translations'],
    'sales-support-materials': ['image', 'document', 'tags', 'tag_types', 'title_translations', 'categories.title_translations', 'sub_categories.title_translations'],
    'marketing-materials': ['image', 'document', 'tags', 'tag_types', 'title_translations', 'categories.title_translations', 'sub_categories.title_translations'],
    'service-supports': ['image', 'document', 'tags', 'tag_types', 'title_translations', 'categories.title_translations', 'sub_categories.title_translations'],
    'product-informations': ['image', 'document', 'tags', 'tag_types', 'categories.title_translations', 'sub_categories.title_translations'],
    'media-templates': ['image', 'document', 'tags', 'tag_types', 'categories.title_translations', 'sub_categories.title_translations'],
    videos: ['image', 'document', 'tags', 'tag_types', 'categories.title_translations', 'sub_categories.title_translations'],
    photos: ['image', 'document', 'tags', 'tag_types', 'categories.title_translations', 'sub_categories.title_translations'],
    documents: ['image', 'document', 'tags', 'tag_types', 'categories.title_translations', 'sub_categories.title_translations'],
}

const buildPopulateParams = (table) => (
    CATEGORY_LIST_POPULATE[table] || []
).map((field) => {
    const [relation, nested] = field.split('.')
    return nested
        ? `populate[${relation}][populate][${nested}]=true`
        : `populate[${relation}]=true`
})

export default function useList({ table, query, adapter, filters, categoryFilter, noAsc, novalide, sortQuery }) {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const [data, setData] = useState([])
    const [pageSize] = useState(50)
    const [page, setPage] = useState(0)
    const [pageCount, setPageCount] = useState(0)
    const [total, setTotal] = useState(0)

    const { filtered, language } = useContext(CoreContext)
    const locale = useMemo(() => languages.find((item) => item.id === language)?.ref || 'en', [language])

    const loadRegisters = useCallback(async () => {
        setLoading(true)
        setError(false)
        const useCategoryQuery = !query && Boolean(CATEGORY_LIST_POPULATE[table])
        const parts = [
            ...Object.entries(filtered || {}).map(([key, value]) =>
                `filters[${key}][$containsi]=${encodeURIComponent(value)}`
            ),
            categoryFilter,
            ...(useCategoryQuery ? buildPopulateParams(table) : []),
        ].filter(Boolean);

        const fil = parts.join("&");
        const sort = noAsc ? '' : (sortQuery || 'sort=title:asc')
        const pagination = `pagination[pageSize]=${pageSize}&pagination[page]=${page + 1}`
        const requestQuery = useCategoryQuery ? '?' : query
        const queryString = useCategoryQuery ? [pagination, fil, sort].filter(Boolean).join("&") : ''
        try {
            const result = novalide
                ? await Read(table, requestQuery, queryString)
                : await apiRequest(() => Read(table, requestQuery, queryString))

            if (!exposeStrapiError(result)) {
                const normalResult = result?.data
                setData(normalResult)
                const p = result?.meta?.pagination;
                setPageCount(p?.pageCount || 0)
                setTotal(p?.total || 0)
            } else {
                setError(true)
            }
        } catch {
            setError(true)
        } finally {
            setLoading(false)
        }

    }, [table, query, page, pageSize, filtered, categoryFilter, noAsc, novalide, sortQuery])
    useEffect(() => {
        setPage(0);
    }, [table, query, categoryFilter, filtered, pageSize]);

    const init = useCallback(async () => {
        await loadRegisters()
    }, [loadRegisters])

    const remove = useCallback(async (item) => {
        setLoading(true)
        const result = await Delete(table, item?.id)
        if (!(result && !exposeStrapiError(result))) {
            setLoading(false)
            return;
        }
        init()
    }, [init, table])

    useEffect(() => {
        init();
    }, [init])

    const registers = useMemo(() => {
        const result = (data || [])?.map(adapter ? (item) => adapter(item, locale) : m => ({
            ...m
        })
        );

        return result
    }, [data, adapter, locale])

    return {
        init,
        loading,
        error,
        registers,
        page,
        pageCount,
        total,
        pageSize,
        setPage,
        retry: init,
        remove
    }
}
